/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.0.0
 */
import { IOptionItemProps } from '@components/common/Select/Select';
import { createAction } from '@modules/app/createAction';

import {
    MODAL_INQUIRY_ATTACH_FILE,
    MODAL_INQUIRY_ATTACH_FILE_FAILED,
    MODAL_INQUIRY_ATTACH_FILE_SUCCESSFUL,
    MODAL_INQUIRY_CLOSE,
    MODAL_INQUIRY_CREATE,
    MODAL_INQUIRY_CREATE_FAILED,
    MODAL_INQUIRY_CREATE_SUCCESSFUL,
    MODAL_INQUIRY_DETACH_FILE,
    MODAL_INQUIRY_DETACH_FILE_FAILED,
    MODAL_INQUIRY_DETACH_FILE_SUCCESSFUL,
    MODAL_INQUIRY_LOAD_TOPIC,
    MODAL_INQUIRY_LOAD_TOPIC_FAILED,
    MODAL_INQUIRY_LOAD_TOPIC_LIST,
    MODAL_INQUIRY_LOAD_TOPIC_LIST_FAILED,
    MODAL_INQUIRY_LOAD_TOPIC_LIST_SUCCESSFUL,
    MODAL_INQUIRY_LOAD_TOPIC_SUCCESSFUL,
    MODAL_INQUIRY_LOAD_USER,
    MODAL_INQUIRY_LOAD_USER_FAILED,
    MODAL_INQUIRY_LOAD_USER_SUCCESSFUL,
} from './createInquiryConstants';
import {
    IModalInquiryAttachFileAction,
    IModalInquiryAttachFileActionPayload,
    IModalInquiryAttachFileSuccessfulAction,
    IModalInquiryCloseAction,
    IModalInquiryCreateAction,
    IModalInquiryCreateActionPayload,
    IModalInquiryCreateFailedAction,
    IModalInquiryCreateSuccessfulAction,
    IModalInquiryCreateSuccessfulPayload,
    IModalInquiryLoadTopicFailedAction,
    IModalInquiryLoadTopicListPayload,
    IModalInquiryLoadTopicPayload,
    IModalInquiryLoadTopicSuccessfulAction,
    IModalInquiryLoadUserPayload,
    TModalInquiryAttachFileFailedAction,
    TModalInquiryDetachFileAction,
    TModalInquiryDetachFileFailedAction,
    TModalInquiryDetachFileSuccessfulAction,
    TModalInquiryLoadTopicAction,
    TModalInquiryLoadTopicListAction,
    TModalInquiryLoadTopicListFailedAction,
    TModalInquiryLoadTopicListSuccessfulAction,
    TModalInquiryLoadUserAction,
    TModalInquiryLoadUserFailedAction,
    TModalInquiryLoadUserSuccessfulAction,
} from './types/createInquiryActionTypes';
import { IInquiryAttachProps, IInquiryUser, ITopicInfo } from './types/createInquiryTypes';


/*
* -------------------------------------------
*          LOAD INQUIRY USER
* -------------------------------------------
*/

export const modalInquiryLoadUser = (payload: IModalInquiryLoadUserPayload): TModalInquiryLoadUserAction =>
    createAction(MODAL_INQUIRY_LOAD_USER, payload);

export const modalInquiryLoadUserSuccessful = (payload: IInquiryUser): TModalInquiryLoadUserSuccessfulAction =>
    createAction(MODAL_INQUIRY_LOAD_USER_SUCCESSFUL, payload);

export const modalInquiryLoadUserFailed = (payload: string): TModalInquiryLoadUserFailedAction =>
    createAction(MODAL_INQUIRY_LOAD_USER_FAILED, payload);


/*
* -------------------------------------------
*          LOAD TOPIC LIST
* -------------------------------------------
*/

export const modalInquiryLoadTopicList =
    (payload: IModalInquiryLoadTopicListPayload): TModalInquiryLoadTopicListAction =>
    createAction(MODAL_INQUIRY_LOAD_TOPIC_LIST, payload);

export const modalInquiryLoadTopicListSuccessful =
    (payload: IOptionItemProps[]): TModalInquiryLoadTopicListSuccessfulAction =>
    createAction(MODAL_INQUIRY_LOAD_TOPIC_LIST_SUCCESSFUL, payload);

export const modalInquiryLoadTopicListFailed = (payload: string): TModalInquiryLoadTopicListFailedAction =>
    createAction(MODAL_INQUIRY_LOAD_TOPIC_LIST_FAILED, payload);


/*
* -------------------------------------------
*          LOAD TOPIC INFO
* -------------------------------------------
*/

export const modalInquiryLoadTopic = (payload: IModalInquiryLoadTopicPayload): TModalInquiryLoadTopicAction =>
    createAction(MODAL_INQUIRY_LOAD_TOPIC, payload);

export const modalInquiryLoadTopicSuccessful = (payload: ITopicInfo): IModalInquiryLoadTopicSuccessfulAction =>
    createAction(MODAL_INQUIRY_LOAD_TOPIC_SUCCESSFUL, payload);

export const modalInquiryLoadTopicFailed = (payload: string): IModalInquiryLoadTopicFailedAction =>
    createAction(MODAL_INQUIRY_LOAD_TOPIC_FAILED, payload);


/*
* -------------------------------------------
*          CREATE INQUIRY
* -------------------------------------------
*/

export const modalInquiryCreate = (payload: IModalInquiryCreateActionPayload): IModalInquiryCreateAction =>
    createAction(MODAL_INQUIRY_CREATE, payload);

export const modalInquiryCreateSuccessful =
    (payload: IModalInquiryCreateSuccessfulPayload): IModalInquiryCreateSuccessfulAction =>
    createAction(MODAL_INQUIRY_CREATE_SUCCESSFUL, payload);

export const modalInquiryCreateFailed = (payload: string): IModalInquiryCreateFailedAction =>
    createAction(MODAL_INQUIRY_CREATE_FAILED, payload);


/*
* -------------------------------------------
*          CLOSE INQUIRY
* -------------------------------------------
*/

export const modalInquiryClose = (): IModalInquiryCloseAction =>
    createAction(MODAL_INQUIRY_CLOSE);


/*
* -------------------------------------------
*          INQUIRY ATTACH FILE
* -------------------------------------------
*/

export const modalInquiryAttachFile = (payload: IModalInquiryAttachFileActionPayload): IModalInquiryAttachFileAction =>
    createAction(MODAL_INQUIRY_ATTACH_FILE, payload);

export const modalInquiryAttachFileSuccessful =
    (payload: IInquiryAttachProps): IModalInquiryAttachFileSuccessfulAction =>
    createAction(MODAL_INQUIRY_ATTACH_FILE_SUCCESSFUL, payload);

export const modalInquiryAttachFileFailed = (payload: string): TModalInquiryAttachFileFailedAction =>
    createAction(MODAL_INQUIRY_ATTACH_FILE_FAILED, payload);


/*
* -------------------------------------------
*          INQUIRY DETACH FILE
* -------------------------------------------
*/

export const modalInquiryDetachFile = (payload: number): TModalInquiryDetachFileAction =>
    createAction(MODAL_INQUIRY_DETACH_FILE, payload);

export const modalInquiryDetachFileSuccessful =
    (payload: IInquiryAttachProps[]): TModalInquiryDetachFileSuccessfulAction =>
    createAction(MODAL_INQUIRY_DETACH_FILE_SUCCESSFUL, payload);

export const modalInquiryDetachFileFailed = (payload: string): TModalInquiryDetachFileFailedAction =>
    createAction(MODAL_INQUIRY_DETACH_FILE_FAILED, payload);


export type CreateInquiryActionsType =
    IModalInquiryCloseAction
    | IModalInquiryCreateAction
    | IModalInquiryCreateFailedAction
    | IModalInquiryCreateSuccessfulAction
    | IModalInquiryAttachFileAction
    | TModalInquiryAttachFileFailedAction
    | IModalInquiryAttachFileSuccessfulAction
    | TModalInquiryDetachFileAction
    | TModalInquiryDetachFileFailedAction
    | TModalInquiryDetachFileSuccessfulAction
    | TModalInquiryLoadUserAction
    | TModalInquiryLoadUserFailedAction
    | TModalInquiryLoadUserSuccessfulAction
    | TModalInquiryLoadTopicAction
    | IModalInquiryLoadTopicFailedAction
    | IModalInquiryLoadTopicSuccessfulAction
    | TModalInquiryLoadTopicListAction
    | TModalInquiryLoadTopicListFailedAction
    | TModalInquiryLoadTopicListSuccessfulAction
    ;
